import { Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { isAuthenticated, handleCallback, startLogin, logout, getUserInfo } from './services/auth';
import ChatPage from './pages/ChatPage';
import AuditPage from './pages/AuditPage';

/** Handle Cognito OAuth callback — exchange code for tokens */
function CallbackHandler() {
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  // The state and verifier are single-use, so the exchange must run once even when React
  // StrictMode runs this effect twice in development.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    // handleCallback validates state and PKCE before exchanging; any failure is shown.
    handleCallback(searchParams)
      .then(() => { window.location.href = '/'; })
      .catch((err) => setError(err.message));
  }, [searchParams]);

  if (error) return <div className="callback-page"><span className="callback-error">Authentication error: {error}</span></div>;
  return <div className="callback-page">Authenticating...</div>;
}

/**
 * Root component: login gate, OAuth callback route, and the signed-in shell.
 *
 * Why: everything except the callback needs a session; the callback must run before one exists.
 * How: unauthenticated users see the sign-in card, which starts the state + PKCE login
 * (`startLogin`); `/callback` validates and exchanges the code; signed-in users get chat, and
 * reviewers (the admin-assigned `reviewer` group in the ID token) also get the audit page.
 *
 * @returns The page for the current authentication state and route.
 */
function App() {
  const authenticated = isAuthenticated();
  const userInfo = getUserInfo();
  const isReviewer = userInfo?.role === 'reviewer';

  // Callback route must be accessible before auth check
  if (!authenticated && window.location.pathname === '/callback') {
    return (
      <Routes>
        <Route path="/callback" element={<CallbackHandler />} />
      </Routes>
    );
  }

  if (!authenticated) {
    return (
      <div className="login-page">
        <div className="login-card">
          <h2>Vestrex App DSS</h2>
          <p>Development Status</p>
          <a
            href="#"
            className="login-btn"
            onClick={(e) => {
              e.preventDefault();
              startLogin().catch(() => { /* navigation failed; the button stays usable */ });
            }}
          >
            Sign In
          </a>
        </div>
      </div>
    );
  }

  return (
    <div>
      <nav className="nav">
        <span className="nav-brand">Hallway</span>
        <a href="/">Chat</a>
        {isReviewer && <a href="/audit">Audit Log</a>}
        <span className="nav-spacer" />
        <span className="nav-email">{userInfo?.email}</span>
        <a href="#" className="sign-out" onClick={(e) => { e.preventDefault(); logout(); }}>Sign Out</a>
      </nav>
      <Routes>
        <Route path="/" element={<ChatPage />} />
        <Route path="/audit" element={isReviewer ? <AuditPage /> : <Navigate to="/" />} />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </div>
  );
}

export default App;
